import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Button } from 'react-bootstrap';
import { FormSelect, DropdownWithGeolocate } from '@components/Common/AwpFormInputs';
import { HaversineDistance } from '@components/Avalanche/OccurrenceHelper';

const ObservationsPreferences = ({ programs, areas, paths, triggerTypes, formikProps, dto }) => {
  const [areaList, setAreaList] = useState(areas);
  const [pathsList, setPathsList] = useState(paths);

  useEffect(() => {
    if (dto?.program) {
      setAreaList(areas.filter((x) => x.parentId === dto.program.value));
    }
    if (dto?.area) {
      setPathsList(paths.filter((x) => x.parentId === dto.area.value));
    }
  }, [dto]);

  const programOnChange = (opt, setFieldValue) => {
    setFieldValue('observations.area', null, false);
    setFieldValue('observations.path', '', false);
    setAreaList(areas.filter((x) => x.parentId === opt.value));

    //path list is unfiltered
    setPathsList(paths);
  };

  const areaOnChange = (opt, setFieldValue) => {
    // filter the paths list to those for this area
    setPathsList(filterList(paths, opt.value));

    //clear path
    setFieldValue('observations.path', '', false);
    setFieldValue(
      'observations.program',
      programs.find((x) => x.value === opt.parentId),
    );
  };

  const filterList = (list, value) => {
    return list.filter(({ parentId }) => {
      return parentId === value;
    });
  };

  const pathOnChange = (opt, setFieldValue, pathListToUse) => {
    //if the user picked a path first, we'll auto select the area/program
    const _area = areas.find(({ value }) => {
      return value === opt.parentId;
    });

    //filter the area list to children of program
    setAreaList(filterList(areas, _area.parentId));

    //set area value
    setFieldValue('observations.area', _area, false);

    //set program state form selected area
    setFieldValue(
      'observations.program',
      programs.find(({ value }) => {
        return value === _area.parentId;
      }),
      false,
    );

    //ensure the paths are children of area
    setPathsList(filterList(pathListToUse || pathsList, _area.value));
  };

  const addDistance = (e, setFieldValue, setFieldTouched) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const crd = pos.coords;

        //HaversineDistance = (lat1, long1, lat2, long2)
        const pathsWithDistance = paths
          .map((p) => {
            const dist = HaversineDistance(p.latitude, p.longitude, crd.latitude, crd.longitude);
            return { ...p, label: `${p.label} (${parseFloat(dist).toFixed(3).toString()}km)`, distanceAway: dist };
          })
          .sort((a, b) => {
            return a.distanceAway - b.distanceAway;
          });

        setPathsList(pathsWithDistance);
        const closestPath = pathsWithDistance.reduce((p, c) => {
          return p.distanceAway < c.distanceAway ? p : c;
        });

        if (closestPath) {
          setFieldValue('observations.path', closestPath);
          setFieldTouched('observations.path', true, true);
          pathOnChange(closestPath, setFieldValue, pathsWithDistance);
        }
      },
      (err) => {
        console.warn(`ERROR(${err.code}): ${err.message}`);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      },
    );
  };

  const handleReset = (formikProps) => {
    formikProps.setFieldValue('observations.program', { value: '' }, false);
    formikProps.setFieldValue('observations.area', { value: '' }, false);
    formikProps.setFieldValue('observations.path', { value: '' }, false);
    formikProps.setFieldValue('observations.triggerType', { value: '' }, false);
    setAreaList(areas);
    setPathsList(paths);
  };

  return (
    <>
      <Card border="light" className="page-card">
        <Card.Header as="h3" style={{ backgroundColor: 'transparent' }}>
          AVALANCHE OBSERVATIONS
        </Card.Header>
        <Card.Body>
          <FormSelect
            className="avalanche-select"
            readOnly={false}
            name="observations.program"
            id="observations.program"
            label="Program"
            postChange={(e) => {
              programOnChange(e, formikProps.setFieldValue);
            }}
            options={programs}
            isMulti={false}
          />

          <FormSelect
            className="avalanche-select"
            readOnly={false}
            name="observations.area"
            id="observations.area"
            label="Avalanche Area"
            postChange={(e) => {
              areaOnChange(e, formikProps.setFieldValue);
            }}
            options={areaList}
            isMulti={false}
          />
          <DropdownWithGeolocate
            readOnly={false}
            name="observations.path"
            id="observations.path"
            label="Path"
            postChange={(e) => {
              pathOnChange(e, formikProps.setFieldValue);
            }}
            options={pathsList}
            isMulti={false}
            buttonAction={(e) => {
              addDistance(e, formikProps.setFieldValue, formikProps.setFieldTouched);
            }}
            disableGPS={false}
          />
          <FormSelect
            readOnly={false}
            name="observations.triggerType"
            id="observations.triggerType"
            label="Trigger Type"
            options={triggerTypes}
            isMulti={false}
          />
          <Button className="my-3" variant="outline-primary" onClick={() => handleReset(formikProps)}>
            Reset
          </Button>
        </Card.Body>
      </Card>
    </>
  );
};

ObservationsPreferences.propTypes = {
  programs: PropTypes.arrayOf(PropTypes.object),
  areas: PropTypes.arrayOf(PropTypes.object),
  paths: PropTypes.arrayOf(PropTypes.object),
  triggerTypes: PropTypes.arrayOf(PropTypes.object),
  formikProps: PropTypes.object,
  dto: PropTypes.object,
};

export default ObservationsPreferences;
